import db from '../db/db'
import { AnalyticsEvents, BookingEventParams } from '../types/AnalyticsEvents'
import Database from '../types/Database'
import {
  BookingCreationData,
  BookingFilterOptions
} from '../types/ServiceTypes'
import { generateDateRange } from '../utils/dateRangeHelpers'
import { formatDateToString } from '../utils/FormatHelpers'

import { AnalyticsService } from './AnalyticsService'
import ErrorHandler from './ErrorHandler'

/**
 * Service for handling booking-related business logic
 * Extracted from components to improve separation of concerns
 *
 * Requirements: 7.5 - Implements consistent error logging
 */
export class BookingService {
  /**
   * Internal method to create a booking without analytics tracking
   * Used by createRecurringBookings to avoid duplicate tracking
   */
  private static async createBookingInternal(
    bookingData: BookingCreationData
  ): Promise<Database.Booking> {
    const { desk, user, office, forDate } = bookingData

    if (!user.uid) {
      throw new Error('User authentication required')
    }

    if (!desk.did || !desk.name) {
      throw new Error('Invalid desk data')
    }

    if (!office.oid) {
      throw new Error('Invalid office data')
    }

    const keyDate = forDate.toISOString().slice(0, 10).replace(/-/g, '')
    const bookingId = `${keyDate}-${office.oid}-${desk.name}`

    const newBooking: Database.Booking = {
      bookingKey: bookingId,
      id: bookingId,
      officeBookingKey: `${keyDate}-${office.oid}`,
      deskName: desk.name,
      did: desk.did,
      officeName: office.name ?? '',
      oid: office.oid,
      forDate: forDate,
      uid: user.uid,
      appver: 2,
      bookerName: `${user.firstname} ${user.lastname?.substring(0, 1)}`
    }

    ErrorHandler.logInfo(
      'Creating booking',
      {
        bookingId: newBooking.id,
        deskName: desk.name,
        officeName: office.name,
        forDate: formatDateToString(forDate),
        userId: user.uid
      },
      { component: 'BookingService', action: 'createBooking' }
    )

    await db.addItem('booking', newBooking)

    return newBooking
  }

  /**
   * Create a new booking
   */
  static async createBooking(
    bookingData: BookingCreationData
  ): Promise<Database.Booking> {
    try {
      const newBooking = await this.createBookingInternal(bookingData)

      // Track booking creation analytics
      // Validates: Requirements 5.1, 5.4, 5.5, 5.6, 5.7, 12.4
      const analyticsParams: BookingEventParams = {
        is_proxy_booking: newBooking.isProxyBooking || false,
        recurrence_type: 'once off',
        days_booked: 1
      }
      AnalyticsService.trackEvent(
        AnalyticsEvents.BOOKING_CREATED,
        analyticsParams
      )

      return newBooking
    } catch (error) {
      ErrorHandler.logSystemError('Error creating booking', error, {
        component: 'BookingService',
        action: 'createBooking'
      })
      throw new Error(`Failed to create booking: ${error}`)
    }
  }

  /**
   * Create multiple bookings for recurring dates
   */
  static async createRecurringBookings(
    desk: Database.Desk,
    user: Database.User,
    office: Database.Office,
    startDate: Date,
    recurAmount: number,
    recurUnit: 'Once off' | 'Same days every week',
    existingBookings: Database.Booking[]
  ): Promise<{
    newBookings: Database.Booking[]
    numNewBookings: number
    numExistingBookings: number
  }> {
    try {
      const dateRange = generateDateRange(startDate, recurUnit, recurAmount)
      const newBookings: Database.Booking[] = []
      let numNewBookings = 0
      let numExistingBookings = 0

      for (const forDate of dateRange) {
        // Check if booking already exists for this date and user
        const existingBooking = existingBookings.find(b => {
          return (
            b.uid === user.uid &&
            b.forDate &&
            formatDateToString(b.forDate) === formatDateToString(forDate)
          )
        })

        if (existingBooking) {
          numExistingBookings++
        } else {
          const newBooking = await this.createBookingInternal({
            desk,
            user,
            office,
            forDate
          })
          newBookings.push(newBooking)
          numNewBookings++
        }
      }

      // Track recurring booking creation analytics
      // Validates: Requirements 5.1, 5.4, 5.5, 5.6, 5.7, 12.4
      if (numNewBookings > 0) {
        const analyticsParams: BookingEventParams = {
          is_proxy_booking: newBookings[0]?.isProxyBooking || false,
          recurrence_type: recurUnit === 'Once off' ? 'once off' : 'weekly',
          days_booked: numNewBookings
        }
        AnalyticsService.trackEvent(
          AnalyticsEvents.BOOKING_CREATED,
          analyticsParams
        )
      }

      return {
        newBookings,
        numNewBookings,
        numExistingBookings
      }
    } catch (error) {
      ErrorHandler.logSystemError('Error creating recurring bookings', error, {
        component: 'BookingService',
        action: 'createRecurringBookings'
      })
      throw new Error(`Failed to create recurring bookings: ${error}`)
    }
  }

  /**
   * Update an existing booking
   */
  static async updateBooking(
    bookingId: string,
    updateData: Partial<Database.Booking>
  ): Promise<Database.Booking> {
    try {
      if (!bookingId) {
        throw new Error('Booking ID is required for update')
      }

      ErrorHandler.logInfo(
        'Updating booking',
        {
          bookingId
        },
        { component: 'BookingService', action: 'updateBooking' }
      )

      // Get the existing booking to determine analytics parameters
      const existingBooking = await db.getById('booking', bookingId)
      if (!existingBooking) {
        throw new Error('Booking not found')
      }

      await db.updateItem('booking', bookingId, updateData)

      // Get the updated booking
      const updatedBooking = await db.getById('booking', bookingId)
      if (!updatedBooking) {
        throw new Error('Failed to retrieve updated booking')
      }

      // Track booking update analytics
      // Validates: Requirements 5.2, 5.4, 5.5, 5.6, 5.7, 12.4
      const analyticsParams: BookingEventParams = {
        is_proxy_booking: updatedBooking.isProxyBooking || false,
        recurrence_type: 'once off', // Individual bookings are always once off
        days_booked: 1
      }
      AnalyticsService.trackEvent(
        AnalyticsEvents.BOOKING_UPDATED,
        analyticsParams
      )

      return updatedBooking
    } catch (error) {
      ErrorHandler.logSystemError('Error updating booking', error, {
        component: 'BookingService',
        action: 'updateBooking'
      })
      throw new Error(`Failed to update booking: ${error}`)
    }
  }

  /**
   * Delete a booking by ID
   */
  static async deleteBooking(bookingId: string): Promise<void> {
    try {
      if (!bookingId) {
        throw new Error('Booking ID is required for deletion')
      }

      // Get the booking before deletion to track analytics
      const booking = await db.getById('booking', bookingId)

      ErrorHandler.logInfo(
        'Deleting booking',
        {
          bookingId
        },
        { component: 'BookingService', action: 'deleteBooking' }
      )

      await db.deleteItem('booking', bookingId)

      // Track booking cancellation analytics
      // Validates: Requirements 5.3, 5.4, 5.5, 5.6, 5.7, 12.4
      if (booking) {
        const analyticsParams: BookingEventParams = {
          is_proxy_booking: booking.isProxyBooking || false,
          recurrence_type: 'once off', // Individual bookings are always once off
          days_booked: 1
        }
        AnalyticsService.trackEvent(
          AnalyticsEvents.BOOKING_CANCELLED,
          analyticsParams
        )
      }
    } catch (error) {
      ErrorHandler.logSystemError('Error deleting booking', error, {
        component: 'BookingService',
        action: 'deleteBooking'
      })
      throw new Error(`Failed to delete booking: ${error}`)
    }
  }

  /**
   * Delete booking for a specific date and user
   * Note: Analytics tracking happens in deleteBooking()
   */
  static async deleteBookingForDate(date: Date, userId: string): Promise<void> {
    try {
      const bookings = await db.query({
        collection: 'booking',
        where: [
          ['uid', '==', userId],
          ['forDate', '==', date]
        ]
      })

      if (bookings.length > 0 && bookings[0].id) {
        // Analytics tracking happens in deleteBooking()
        await this.deleteBooking(bookings[0].id)
      }
    } catch (error) {
      ErrorHandler.logSystemError('Error deleting booking for date', error, {
        component: 'BookingService',
        action: 'deleteBookingForDate'
      })
      throw new Error(`Failed to delete booking for date: ${error}`)
    }
  }

  /**
   * Get bookings with optional filtering
   */
  static async getBookings(
    filters: BookingFilterOptions = {}
  ): Promise<Database.Booking[]> {
    try {
      const whereConditions: any[] = []

      if (filters.userId) {
        whereConditions.push(['uid', '==', filters.userId])
      }

      if (filters.officeId) {
        whereConditions.push(['oid', '==', filters.officeId])
      }

      if (filters.deskId) {
        whereConditions.push(['did', '==', filters.deskId])
      }

      if (filters.startDate) {
        whereConditions.push(['forDate', '>=', filters.startDate])
      }

      if (filters.endDate) {
        whereConditions.push(['forDate', '<=', filters.endDate])
      }

      const bookings = await db.query({
        collection: 'booking',
        where: whereConditions.length > 0 ? whereConditions : undefined
      })

      return bookings
    } catch (error) {
      ErrorHandler.logNetworkError('Error getting bookings', error, {
        component: 'BookingService',
        action: 'getBookings'
      })
      throw new Error(`Failed to get bookings: ${error}`)
    }
  }

  /**
   * Get user's bookings
   */
  static async getUserBookings(userId: string): Promise<Database.Booking[]> {
    try {
      return await this.getBookings({ userId })
    } catch (error) {
      ErrorHandler.logNetworkError('Error getting user bookings', error, {
        component: 'BookingService',
        action: 'getUserBookings'
      })
      throw new Error(`Failed to get user bookings: ${error}`)
    }
  }

  /**
   * Get bookings for a specific office
   */
  static async getOfficeBookings(
    officeId: string
  ): Promise<Database.Booking[]> {
    try {
      return await this.getBookings({ officeId })
    } catch (error) {
      ErrorHandler.logNetworkError('Error getting office bookings', error, {
        component: 'BookingService',
        action: 'getOfficeBookings'
      })
      throw new Error(`Failed to get office bookings: ${error}`)
    }
  }

  /**
   * Get bookings for a date range
   */
  static async getBookingsForDateRange(
    startDate: Date,
    endDate: Date,
    officeIds?: string[]
  ): Promise<Database.Booking[]> {
    try {
      const filters: BookingFilterOptions = {
        startDate,
        endDate
      }

      if (officeIds && officeIds.length > 0) {
        // For multiple office IDs, we need to query each separately and combine
        const allBookings: Database.Booking[] = []
        for (const officeId of officeIds) {
          const officeBookings = await this.getBookings({
            ...filters,
            officeId
          })
          allBookings.push(...officeBookings)
        }
        return allBookings
      }

      return await this.getBookings(filters)
    } catch (error) {
      ErrorHandler.logNetworkError(
        'Error getting bookings for date range',
        error,
        {
          component: 'BookingService',
          action: 'getBookingsForDateRange'
        }
      )
      throw new Error(`Failed to get bookings for date range: ${error}`)
    }
  }

  /**
   * Check if a desk is available for a specific date
   */
  static async isDeskAvailable(deskId: string, date: Date): Promise<boolean> {
    try {
      const bookings = await db.query({
        collection: 'booking',
        where: [
          ['did', '==', deskId],
          ['forDate', '==', date]
        ]
      })

      return bookings.length === 0
    } catch (error) {
      ErrorHandler.logNetworkError('Error checking desk availability', error, {
        component: 'BookingService',
        action: 'isDeskAvailable'
      })
      throw new Error(`Failed to check desk availability: ${error}`)
    }
  }

  /**
   * Validate booking data before creation
   */
  static validateBookingData(bookingData: BookingCreationData): string[] {
    const errors: string[] = []
    const { desk, user, office, forDate } = bookingData

    if (!user.uid) {
      errors.push('User authentication required')
    }

    if (!desk.did || !desk.name) {
      errors.push('Valid desk information required')
    }

    if (!office.oid || !office.name) {
      errors.push('Valid office information required')
    }

    if (!forDate || forDate < new Date()) {
      errors.push('Valid future date required')
    }

    return errors
  }
}

export default BookingService
